import {
  AsyncPropSelector,
  Generator,
  ApiProgressLoader,
  useProduct
} from '@nile/components';
import { AccountClient } from '@nile/graphql';
import { DisabledTag } from '@nile/mangifera/src/Pebbles/TicketContainer';
import { Popover, Button, ButtonGroup } from '@superops/ui-design-system';
import { Edit } from '@styled-icons/material/Edit';
import gql from 'graphql-tag';
import React, { useCallback, useState, useContext } from 'react';
import styled from 'styled-components';
import { useLazyQuery, useQuery } from '@apollo/client';
import { GET_CLIENT_USERS } from '@nile/graphql/src/Query/client';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import { EditIcon } from '@nile/components/src/Icons';
import { getTechnicianVariables } from '@nile/graphql/src/Constants/variablesResolver';

const Wrap = styled.div`
  display: flex;
  align-items: center;
  .edit-actions {
    visibility: hidden;
    cursor: pointer;
  }
  padding-left: 8px;
  padding-right: 8px;
  max-width: 100%;
`;

const Field = styled.div`
  min-width: 200px;
  max-width: 250px;
  flex-grow: 1;
  padding-right: 10px;
  padding-left: 10px;
  margin-top: 8px;
  margin-bottom: 8px;
  .Polaris-ButtonGroup {
    margin-top: 12px;
  }
`;

function DualCell(props) {
  const ticketContext = useContext(TicketContext);
  const { cell, params, _ticket } = props;
  const allProps = params || cell.column;
  const fieldName = params?.field?.name
    ? params?.field?.name
    : props?.cell?.value?.name
    ? props?.cell?.value?.name
    : '';
  const {
    editable,
    parentField,
    dependantField,
    actions,
    accessor,
    id
  } = allProps;
  const ticket = _ticket || cell.row.original;
  const clientIds = ticket.client?.accountId
    ? [ticket.client?.accountId]
    : null;
  const siteIds = ticket?.site?.id ? [ticket.site.id] : null;
  const currentProduct = useProduct();
  const [popoverActive, setPopoverActive] = useState(false);
  const [updatedProperties, setUpdatedProperties] = useState({});
  const [apiProgress, setApiProgress] = useState(false);
  const [apiResult, setApiResult] = useState('');
  const togglePopoverActive = useCallback(() => {
    setPopoverActive(!popoverActive);
  });

  const activator = <EditIcon onClick={() => togglePopoverActive()} />;

  const updateSelectedProperties = (key, value) => {
    setUpdatedProperties({
      ...updatedProperties,
      [key]: value
    });
  };

  const updateProperties = (properties) => {
    setUpdatedProperties({
      ...updatedProperties,
      ...properties
    });
  };

  const setApiComplete = (state) => {
    if (props.showProgress) {
      setApiProgress(false);
      setApiResult(state);
    }
    setTimeout(() => {
      setApiResult('');
    }, 2000);
  };

  const updateSelection = (f, val) => {
    let serialisedValue = null;
    let updatedProperty = {};
    if (f.isCustom) {
      updateSelectedProperties('customFields', val);
    } else {
      switch (f.name) {
        case 'techGroup':
          serialisedValue = { groupId: val.groupId, name: val.name };
          break;
        case 'technician':
          serialisedValue = {
            userId: val.userId,
            firstName: val.firstName,
            lastName: val.lastName
          };
          break;
        case 'client':
          serialisedValue = { accountId: val.accountId, name: val.name };
          break;
        case 'requester':
          serialisedValue = {
            userId: val.userId,
            firstName: val.firstName,
            lastName: val.lastName
          };
          break;
        default:
          serialisedValue = val.name;
      }

      updatedProperty = { [f.name]: serialisedValue };
      if (parentField.name === f.name) {
        updatedProperty = {
          ...updatedProperty,
          [dependantField.name]: null
        };
      }
      updateProperties(updatedProperty);
    }
  };

  const getVariables = (type) => {
    let variables = {
      clientIds: {
        value: clientIds,
        type: '[ID!]'
      }
    };
    switch (type) {
      case 'getAllowedTechnicianGroups':
        return {
          input: {
            type: 'GetAllowedTechnicianGroupsInput!'
          }
        };
      case 'techGroupUsers':
        return {
          input: {
            type: 'GetTechnicianListInput!'
          }
        };
      default:
        return variables;
    }
  };

  const getGroupFields = () => {
    return [
      'groupId',
      'name',
      {
        userList: ['userId', 'email', 'firstName', 'lastName']
      }
    ];
  };
  const getClientVariables = () => {
    let variables = {
      listInfo: {
        type: 'ListInfoInput!'
      }
    };
    return variables;
  };

  const getRequesterVariables = () => {
    let variables = {
      accountId: {
        type: 'ID!'
      }
    };
    return variables;
  };

  const getClientFields = () => {
    return [
      {
        clients: ['accountId', 'name']
      }
    ];
  };
  const getrequesterGroupFields = () => {
    return ['userId', 'firstName', 'lastName', 'email', 'name'];
  };

  const getTechnicianFields = () => {
    return [
      {
        userList: ['userId', 'email', 'firstName', 'lastName', 'name']
      }
    ];
  };

  const dynamicGroupQuery = () => {
    switch (allProps.field.name) {
      case 'client':
      case 'requester':
        let generatorClient = new Generator({
          operation: 'getClientList',
          variables: getClientVariables(),
          fields: getClientFields()
        });
        const queryStringClient = generatorClient.queryBuilder();

        return AccountClient.query({
          query: gql`
            ${queryStringClient.query}
          `,
          fetchPolicy: 'network-only',
          variables: {
            listInfo: {
              sort: [
                {
                  attribute: 'name',
                  order: 'ASC'
                }
              ]
            }
          }
        });
      default:
        let generator = new Generator({
          operation: 'getAllowedTechnicianGroups',
          variables: getVariables('getAllowedTechnicianGroups'),
          fields: getGroupFields()
        });
        const queryString = generator.queryBuilder();

        return AccountClient.query({
          query: gql`
            ${queryString.query}
          `,
          fetchPolicy: 'network-only',
          variables: getTechnicianVariables({
            clientIds,
            siteIds,
            currentProduct
          })
        });
    }
  };
  const dynamicTechnicianQuery = () => {
    switch (allProps.dependantField.name) {
      case 'requester':
        let requesterClientId = updatedProperties.hasOwnProperty('client')
          ? updatedProperties?.client?.accountId
          : ticket?.client?.accountId;
        let generatorClientRequester = new Generator({
          operation: 'getClientUsers',
          variables: getRequesterVariables(),
          fields: getrequesterGroupFields()
        });
        const queryStringClientRequester = generatorClientRequester.queryBuilder();

        return AccountClient.query({
          query: gql`
            ${queryStringClientRequester.query}
          `,
          fetchPolicy: 'network-only',
          variables: {
            accountId: requesterClientId
          }
        });

      default:
        let groupName = updatedProperties.hasOwnProperty('techGroup')
          ? updatedProperties?.techGroup?.name
          : ticket?.techGroup?.name;
        if (groupName) {
          return dynamicGroupQuery();
        } else {
          let generator = new Generator({
            operation: 'getTechnicianList',
            variables: getVariables('techGroupUsers'),
            fields: getTechnicianFields()
          });
          const queryString = generator.queryBuilder();

          return AccountClient.query({
            query: gql`
              ${queryString.query}
            `,
            variables: getTechnicianVariables({
              clientIds,
              siteIds,
              currentProduct,
              isListInfoRequired: true
            })
          });
        }
    }
  };

  const getDisplayString = (key, element) => {
    if (typeof key === 'object') {
      return `${element[key[0]]} ${element[key[1]]}`; //todo vignesh loop
    } else {
      return element[key];
    }
  };

  const generateGroupOptions = (field, data) => {
    let options = [];
    let allValues = [];
    let key = 'name';
    if (data) {
      switch (field.name) {
        case 'client':
          options = data ? data['getClientList'].clients : [];
          allValues = options.map((element) => ({
            ...element,
            name: getDisplayString(key, element),
            value: getDisplayString(key, element),
            label: getDisplayString(key, element)
          }));
          break;
        case 'requester':
          options = data ? data['getClientUsers'] : [];
          allValues = options.map((element) => ({
            ...element,
            name: getDisplayString(key, element),
            value: getDisplayString(key, element),
            label: getDisplayString(key, element)
          }));
          break;
        case 'techGroup':
          options = data ? data['getAllowedTechnicianGroups'] : [];
          allValues = options.map((element) => ({
            ...element,
            name: getDisplayString(key, element),
            value: getDisplayString(key, element),
            label: getDisplayString(key, element)
          }));
          break;
        case 'technician':
          let groupName = updatedProperties.hasOwnProperty('techGroup')
            ? updatedProperties?.techGroup?.name
            : ticket?.techGroup?.name;
          if (groupName) {
            const selectedTechGroup = data.getAllowedTechnicianGroups
              ? data.getAllowedTechnicianGroups.find(
                  (el) => el?.name === groupName
                )
              : null;
            options = selectedTechGroup ? selectedTechGroup?.userList : null;
            key = ['firstName', 'lastName'];
            allValues = options
              ? options.map((element) => ({
                  ...element,
                  name: getDisplayString(key, element),
                  value: getDisplayString(key, element),
                  label: getDisplayString(key, element)
                }))
              : [];
          } else {
            options = data ? data.getTechnicianList?.userList : [];
            key = ['firstName', 'lastName'];
            allValues = options.map((element) => ({
              ...element,
              name: getDisplayString(key, element),
              value: getDisplayString(key, element),
              label: getDisplayString(key, element)
            }));
          }
          break;
        default:
      }
    }

    return allValues;
  };

  const getTag = () => {
    const parentFieldValue = updatedProperties.hasOwnProperty(parentField.name)
      ? updatedProperties[parentField.name]
      : ticket[parentField.name];
    const defaultParentValue = parentFieldValue
      ? {
          ...parentFieldValue,
          name: `${parentFieldValue.name}`,
          label: `${parentFieldValue.name}`,
          value: `${parentFieldValue.name}`
        }
      : '';
    const dependantFieldValue = updatedProperties.hasOwnProperty(
      dependantField.name
    )
      ? updatedProperties[dependantField.name]
      : ticket[dependantField.name];
    const concatFnameLname = (fieldValue) => {
      return fieldValue.firstName && fieldValue.lastName
        ? `${fieldValue.firstName} ${fieldValue.lastName}`
        : fieldValue.firstName;
    };
    const defaultDependantValue = dependantFieldValue
      ? {
          name: concatFnameLname(dependantFieldValue),
          label: concatFnameLname(dependantFieldValue),
          value: concatFnameLname(dependantFieldValue)
        }
      : '';

    return (
      <Field>
        <AsyncPropSelector
          generateOptions={generateGroupOptions}
          dynamicQuery={clientIds || siteIds ? dynamicGroupQuery : null}
          isDynamicQuery
          defaultValue={defaultParentValue}
          updateSelection={updateSelection}
          ticket={ticket}
          field={parentField}
        />
        <AsyncPropSelector
          generateOptions={generateGroupOptions}
          dynamicQuery={dynamicTechnicianQuery}
          isDynamicQuery
          defaultValue={defaultDependantValue}
          updateSelection={updateSelection}
          ticket={ticket}
          field={dependantField}
        />
        <div className="mt10">
          <ButtonGroup>
            <Button
              onClick={() => {
                togglePopoverActive();
                setUpdatedProperties({});
              }}
            >
              Cancel
            </Button>
            <Button
              primary
              onClick={() => {
                if (props.showProgress) {
                  setApiProgress(true);
                }
                actions.save &&
                  actions.save(
                    updatedProperties,
                    ticket,
                    props.data,
                    setApiComplete
                  );
                togglePopoverActive();
                setUpdatedProperties({});
              }}
            >
              Save
            </Button>
          </ButtonGroup>
        </div>
      </Field>
    );
  };
  //TODO :: render value in a proper way for NA - vignesh
  const field = cell
    ? cell.row?.values?.[cell.column.id] || {}
    : ticket[accessor];
  const displayVal =
    field?.firstName && field?.lastName
      ? `${field.firstName} ${field.lastName}`
      : field?.firstName
      ? field.firstName
      : field?.name
      ? field.name
      : '';

  const openNewTab = (props) => {
    switch (props.openTabFor) {
      case 'client':
        ticketContext.openTab({
          module: 'CLIENT',
          data: {
            id: props.params.accountId,
            name: props.params.name,
            value: props.params.name
          }
        });
        break;
      default:
    }
  };
  /*If the ticket is created by system we are getting requister value NA.
  We are handing that instead of NA we use show SYSTEM */
  const renderValue = () => {
    return displayVal
      ? displayVal
      : fieldName === 'requester' && ticket.source === 'Agent'
      ? 'System'
      : <DisabledTag>NA</DisabledTag> || '--';
  };
  const FieldInfo = (props) => {
    const { action, params, openTabFor, ticketSource } = props;
    return (
      <>
        <Value
          className="sops-no-bold"
          link={openTabFor === 'client'}
          onClick={() => openNewTab(props)}
        >
          {renderValue()}
        </Value>
      </>
    );
  };
  const Value = styled.div`
    line-height: 16px;
    font-weight: bold;
    color: ${(props) => (props.link ? 'blue' : 'inherit')};
    white-space: nowrap;
    max-width: 250px;
    text-overflow: ellipsis;
    overflow: hidden;
    cursor: ${(props) => (props.link ? 'pointer' : 'default')};
  `;

  return !popoverActive ? (
    <Wrap className={`${editable ? 'editable-cell' : 'cell'}`}>
      <div className="ellipsis" title={displayVal}>
        <FieldInfo
          params={ticket.client}
          value={displayVal}
          openTabFor={fieldName}
          ticketSource={ticket.source}
        />
      </div>
      {editable && <div className="edit-actions mr-10">{activator}</div>}
      {apiProgress && <ApiProgressLoader isLoading />}
      {apiResult && <ApiProgressLoader isSuccess={apiResult === 'success'} />}
    </Wrap>
  ) : (
    <Wrap>
      <div className="ellipsis" title={displayVal}>
        {renderValue()}
      </div>
      <Popover
        activator={activator}
        active={popoverActive}
        onClose={() => {
          return;
          //togglePopoverActive()
        }}
      >
        {getTag()}
      </Popover>
    </Wrap>
  );
}

export default DualCell;
